import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from "@stencil/core";

import { StzhInputBlurEvent, StzhInputChangedEvent, StzhInputChangeEvent, StzhInputFocusEvent } from "../../index";

import { hasSlot } from "../../utils/utils";
import { StzhLocaleComponent } from "../../index";

import { StzhInputDescription } from "./stzh-input-description";

let inputCounter = 0;

/**
 * @slot - Slot for label content
 * @slot description - Slot for description
 * @slot description-long - Slot for long description (in popover)
 * @slot description-long-title - Slot for long description title (in popover) / instead of descriptionLongTitle or description property
 * @slot error - Slot for error
 * @slot button-right - Slot for right button
 */
@Component({
  tag: "stzh-input",
  styleUrl: "stzh-input.scss",
  scoped: true,
})
export class StzhInput {
  /** Translation strings */
  @Prop() localization: StzhLocaleComponent;

  /** Whether the input is a textarea or an input field */
  @Prop() multiline: boolean = false;

  /** Rows attribute if input element is textarea */
  @Prop() rows: number;

  /** If input field, this defines the type (if `search`, a search icon will be displayed) */
  @Prop({ reflect: true }) type: "text" | "search" | "password" | "email" | "url" | "tel" | "number" | "hidden" =
    "text";

  /** If input type is number, this will be the step size  */
  @Prop() step: number = 1;

  /** If input type is number, this will be the minimum step number  */
  @Prop() min: number;

  /** If input type is number, this will be the maximum step number  */
  @Prop() max: number;

  /** If input type is number and this property is true, this will show the spin buttons on hover/focus */
  @Prop({ reflect: true }) showSpin: boolean = false;

  /** Whether the element is readonly or not */
  @Prop({ reflect: true }) readonly: boolean = false;

  /** Whether the element is disabled or not */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** Whether clearable button is showing when input field has value and is enabled (button-right slot not usable anymore when true) */
  @Prop({ reflect: true }) clearable: boolean = false;

  /** Hide `(optional)` label (or use `required` inside form to hide it) */
  @Prop({ reflect: true }) hideOptional: boolean = false;

  /** Minimum of characters */
  @Prop() minlength: number;

  /** Maxlength of characters */
  @Prop() maxlength: number;

  /** The name of the input element */
  @Prop({ reflect: true }) name: string;

  /** The value of the input element */
  @Prop({ mutable: true }) value: string = "";

  /** Default value (used by reset) */
  @Prop({ mutable: true }) defaultValue: string = "";

  /** Description message (use description slot as alternative) */
  @Prop() description: string;

  /** Long description message appearing in a popover (use description-long slot as alternative) */
  @Prop() descriptionLong: string;

  /**
   * **Do not use this property. It will be deprecated with the next major release.**
   * Long description title appearing in a popover (use description-long-title slot as or description property alternative) */
  @Prop() descriptionLongTitle: string;

  /** One or multiple error message (use error slot as alternative) */
  @Prop() error: string | string[];
  private _error: string[];

  /** Invalid status */
  @Prop({ reflect: true }) invalid: boolean = false;

  /** Required status */
  @Prop({ reflect: true }) required: boolean = false;

  /**
   * Specify inputmode.
   * See [MDN](https://developer.mozilla.org/de/docs/Web/HTML/Global_attributes/inputmode) for possible values
   */
  @Prop() inputmode: string;

  /**
   * Specify what the user agent has to provide as automated assistance.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete) for possible values
   */
  @Prop() autocomplete: string = "on";

  /** Whether autocomplete should be turned off  */
  @Prop() noAutocomplete: boolean = false;

  /** Label */
  @Prop() label: string = "";

  /** Whether label should be truncate or not */
  @Prop() noLabelTruncate: boolean = false;

  /** Whether label should be visually hidden. */
  @Prop() labelHidden: boolean = false;

  /** Size variant */
  @Prop({ reflect: true }) size: "default" | "small" = "default";

  /** Id for element which describes the input (this will be overwritten if description prop or slot is used, used by stzh-radiogroup) */
  @Prop({ attribute: "a11y-describedby" }) a11yDescribedby: string = "";

  /** Whether the input is filled or not */
  @State() filled: boolean;

  /** Whether to show the password or not */
  @State() showPassword: boolean;

  @Element() element: HTMLStzhInputElement;

  /** Input change event */
  @Event() stzhChange: EventEmitter<StzhInputChangeEvent>;

  /** Input changed event */
  @Event() stzhChanged: EventEmitter<StzhInputChangedEvent>;

  /** Input focus event */
  @Event() stzhFocus: EventEmitter<StzhInputFocusEvent>;

  /** Input blur event */
  @Event() stzhBlur: EventEmitter<StzhInputBlurEvent>;

  @Listen("reset", { target: "document" })
  resetListener(event: Event) {
    if ((event.target as HTMLElement).contains(this.element)) {
      requestAnimationFrame(() => {
        this.handleReset();
      });
    }
  }

  @Watch("value")
  valueWatcher(newValue: string) {
    this.filled = newValue !== "";
  }

  @Watch("error")
  errorWatcher(newValue: string | string[]) {
    if (typeof newValue === "string") {
      try {
        this._error = JSON.parse(newValue);
      } catch (e) {
        if (newValue) {
          this._error = [newValue];
        } else {
          this._error = [];
        }
      }
    } else if (newValue) {
      this._error = newValue;
    } else {
      this._error = [];
    }
  }

  @Watch("type")
  typeWatcher(newValue: string) {
    const types = ["text", "search", "password", "email", "url", "tel", "number", "hidden"];

    if (!this.multiline && !types.includes(newValue)) {
      throw new Error(`Type ${newValue} not supported`);
    }
  }

  private input: HTMLInputElement | HTMLTextAreaElement;
  private inputId: string;
  private focusedByInput: boolean = false;
  private renderPromiseResolve: (value?: unknown) => void;

  private waitForNextRender() {
    return new Promise(resolve => (this.renderPromiseResolve = resolve));
  }

  private onClearClick = async (event: MouseEvent) => {
    this.value = "";
    this.input.focus();

    await this.waitForNextRender();

    this.stzhChange.emit({
      component: "stzh-input",
      originalEvent: event,
      value: this.value,
    });

    this.stzhChanged.emit({
      component: "stzh-input",
      originalEvent: event,
      value: this.value,
    });
  };

  private onTogglePasswordVisibilityClick = async () => {
    this.showPassword = !this.showPassword;
  };

  private handleReset = async () => {
    this.value = this.defaultValue;
    // we also update value, since render doesn't get executed sometimes
    this.input.value = this.defaultValue;

    // TODO: check if we need own reset event or trigger the change event
    // (custom browser behaviour doesn't send change event, when reset is executed)
    // this.stzhChange.emit({
    //   component: "stzh-input",
    //   originalEvent: event,
    //   value: this.value
    // });
  };

  private onInput = (event: InputEvent) => {
    this.value = this.input.value;

    this.stzhChange.emit({
      component: "stzh-input",
      originalEvent: event,
      value: this.value,
    });
  };

  private onChange = (event: InputEvent) => {
    this.value = this.input.value;

    this.stzhChanged.emit({
      component: "stzh-input",
      originalEvent: event,
      value: this.value,
    });
  };

  private onRootFocus = () => {
    if (!this.focusedByInput) {
      this.input.focus();
    }

    this.focusedByInput = false;
  };

  private onFocus = (event: FocusEvent) => {
    this.focusedByInput = true;

    const focusEvent = new FocusEvent("focus", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(focusEvent);
    this.stzhFocus.emit({
      component: "stzh-input",
      originalEvent: event,
    });
  };

  private onBlur = (event: FocusEvent) => {
    // fixes weird bug where autofill selection doesn't get removed
    this.input.value = this.input.value;

    const blurEvent = new FocusEvent("blur", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(blurEvent);
    this.stzhBlur.emit({
      component: "stzh-input",
      originalEvent: event,
    });
  };

  private renderTextarea(): HTMLTextAreaElement {
    return (
      <textarea
        class="stzh-input__input stzh-input__input--textarea"
        ref={el => (this.input = el as HTMLTextAreaElement)}
        id={this.inputId}
        name={this.name}
        value={this.value}
        rows={this.rows}
        disabled={this.disabled}
        readonly={this.readonly}
        minlength={this.minlength}
        maxlength={this.maxlength}
        aria-describedby={`${this.inputId}-description ${this.a11yDescribedby}`}
        aria-required={this.required ? "true" : "false"}
        aria-invalid={this.invalid ? "true" : "false"}
        onChange={this.onChange}
        onInput={this.onInput}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
      ></textarea>
    );
  }

  private renderInput(): HTMLInputElement {
    return (
      <input
        class="stzh-input__input stzh-input__input--input"
        ref={el => (this.input = el as HTMLInputElement)}
        id={this.inputId}
        type={this.showPassword ? "text" : this.type}
        name={this.name}
        value={this.value}
        defaultValue={this.defaultValue}
        min={this.min}
        max={this.max}
        step={this.step}
        inputmode={this.inputmode}
        disabled={this.disabled}
        readonly={this.readonly}
        minlength={this.minlength}
        maxlength={this.maxlength}
        autocomplete={this.noAutocomplete ? "off" : this.autocomplete}
        aria-autocomplete={this.noAutocomplete ? "none" : undefined}
        aria-describedby={`${this.inputId}-description ${this.a11yDescribedby}`}
        aria-required={this.required ? "true" : "false"}
        aria-invalid={this.invalid ? "true" : "false"}
        onChange={this.onChange}
        onInput={this.onInput}
        onFocus={this.onFocus}
        onBlur={this.onBlur}
      />
    );
  }

  componentDidRender() {
    if (this.renderPromiseResolve) {
      this.renderPromiseResolve();
    }
  }

  async componentWillLoad() {
    this.inputId = `stzh-input-${inputCounter++}`;
    this.typeWatcher(this.type);
    this.valueWatcher(this.value);
    this.errorWatcher(this.error);

    this.defaultValue = this.defaultValue || this.value;

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "input");
    }
  }

  render() {
    const buttonRightSlotUsed = hasSlot(this.element, "button-right");
    const descriptionUsed = hasSlot(this.element, "description") || !!this.description;
    const descriptionLongUsed = hasSlot(this.element, "description-long") || !!this.descriptionLong;
    const errorUsed = hasSlot(this.element, "error") || this._error?.length > 0;

    const classes = {
      "stzh-input": true,
      "stzh-input--has-button-right":
        buttonRightSlotUsed ||
        this.type === "search" ||
        (this.clearable && this.filled && !this.readonly && !this.disabled),
      "stzh-input--has-description": descriptionUsed,
      "stzh-input--has-description-long": descriptionLongUsed,
      "stzh-input--has-error": errorUsed,
      "stzh-input--has-spin": this.showSpin,
      "stzh-input--is-invalid": this.invalid || errorUsed,
      "stzh-input--is-disabled": this.disabled,
      "stzh-input--is-readonly": this.readonly,
      "stzh-input--is-required": this.required,
      "stzh-input--is-filled": this.filled,
      "stzh-input--is-not-filled": !this.filled,
      "stzh-input--is-clearable": this.clearable,
      "stzh-input--is-not-clearable": !this.clearable,
      "stzh-input--has-label-hidden": this.labelHidden,
      [`stzh-input--size-${this.size}`]: !!this.size,
      [`stzh-input--type-${this.type}`]: !!this.type,
    };

    return (
      <Host
        is-invalid={this.invalid || errorUsed}
        tabindex={this.disabled ? null : "-1"}
        onFocus={this.onRootFocus}
        is-filled={this.filled}
      >
        <div class={classes}>
          <label
            class={{
              "stzh-input__label": true,
              "no-label-truncate": this.noLabelTruncate,
            }}
            htmlFor={this.inputId}
          >
            {this.label ? this.label : <slot></slot>}
            {!this.hideOptional && (
              <span class="stzh-input__marker">
                <span class="stzh-input__marker-symbol" aria-hidden="true">
                  {this.required
                    ? this.localization.$globals.requiredFieldMarker
                    : this.localization.$globals.optionalFieldMarker}
                </span>
                <span class="stzh-input__marker-text">
                  {this.required
                    ? this.localization.$globals.requiredFieldText
                    : this.localization.$globals.optionalFieldText}
                </span>
              </span>
            )}
          </label>
          <div class="stzh-input__field-wrapper">
            {this.multiline ? this.renderTextarea() : this.renderInput()}
            {this.clearable || this.type === "search" || this.type === "password" ? (
              (!this.clearable || this.disabled || this.readonly || !this.filled) &&
              (this.type === "search" || this.type === "password") ? (
                <stzh-button
                  class="stzh-input__button-right"
                  icon={this.type === "search" ? "search" : "view"}
                  size={this.size}
                  iconOnly={true}
                  onClick={this.onTogglePasswordVisibilityClick}
                  disabled={this.disabled || this.readonly}
                ></stzh-button>
              ) : this.filled ? (
                <stzh-button
                  class="stzh-input__button-right"
                  icon="close"
                  size={this.size}
                  iconOnly={true}
                  onClick={this.onClearClick}
                  a11yLabel={this.localization.$globals.clearButtonLabel}
                  aria-controls={this.inputId}
                ></stzh-button>
              ) : (
                <slot name="button-right"></slot>
              )
            ) : (
              <slot name="button-right"></slot>
            )}
          </div>
          {(errorUsed || this.description) && (
            <StzhInputDescription
              classPrefix="stzh-input"
              id={`${this.inputId}-description`}
              error={this._error}
              description={this.description}
              descriptionLong={this.descriptionLong}
              descriptionLongTitle={this.descriptionLongTitle}
              descriptionLongUsed={descriptionLongUsed}
              moreInfoButtonLabel={this.localization.$globals.moreInfoButtonLabel}
            />
          )}
        </div>
      </Host>
    );
  }
}
